import React from 'react';
import { CheckoutError, type CheckoutResponseDto, PaymentMethodEnum } from '../../../services/checkout/checkout.types';
import type { FormData } from '../../../types';
import type { ShopifyOfferDto } from '../../../offer.types';
import { useErrorAlerts } from '../../../contexts/ErrorContext';
import PixPaymentView from '../../PaymentViews/PixPaymentView';
import BoletoPaymentView from '../../PaymentViews/BoletoPaymentView';
import CreditCardView from '../../PaymentViews/CreditCardView';

interface CheckoutViewsProps {
  orderResponse: CheckoutResponseDto | null;
  formData: FormData;
  shopifyOffer: ShopifyOfferDto;
  loading: Set<string>;
  setLoading: (loading: Set<string>) => void;
}

export const CheckoutViews: React.FC<CheckoutViewsProps> = ({
  orderResponse,
  formData,
  shopifyOffer,
  loading,
  setLoading,
}) => {
  const errorsContext = useErrorAlerts();

  const renderPaymentView = () => {
    if (loading.has('initial')) {
      const newLoading = new Set(loading);
      newLoading.delete('initial');
      if (newLoading.size !== loading.size) { // Avoid infinite loop if delete fails
        setLoading(newLoading);
      }
    }

    if (orderResponse) {
      // Em reload de thank-you, o formData pode estar vazio; usar total do pedido como fonte primária.
      const value = Number(orderResponse.order?.totalPrice || formData.orderSummary.total || 0);

      switch (orderResponse.paymentMethod.paymentMethod) {
        case PaymentMethodEnum.PIX:
          // ✅ LOG BACKEND: Tentando renderizar PIX
          if (typeof window !== 'undefined' && (window as any).logToBackend) {
            (window as any).logToBackend(`Tentando renderizar PIX - paymentData: ${JSON.stringify({
              hasPaymentData: !!orderResponse.paymentData,
              hasDetails: !!orderResponse.paymentData?.details,
              paymentDataKeys: orderResponse.paymentData ? Object.keys(orderResponse.paymentData) : 'null',
              detailsKeys: orderResponse.paymentData?.details ? Object.keys(orderResponse.paymentData.details) : 'null'
            })}`, 'loading');
          }
          
          if (orderResponse.paymentData?.details) {
            // ✅ LOG BACKEND: PIX renderizado com sucesso
            if (typeof window !== 'undefined' && (window as any).logToBackend) {
              (window as any).logToBackend('PIX renderizado com sucesso - redirecionando para PixPaymentView', 'success');
            }
            
            return (
              <PixPaymentView
                paymentDetails={orderResponse.paymentData.details}
                customerName={`${orderResponse.customer.firstName} ${orderResponse.customer.lastName}`}
                totalAmount={value}
                offerNanoId={shopifyOffer.nanoId}
                shopifyData={shopifyOffer}
              />
            );
          }
          
          // ✅ LOG BACKEND: Falha ao renderizar PIX
          if (typeof window !== 'undefined' && (window as any).logToBackend) {
            (window as any).logToBackend('Falha ao renderizar PIX - detalhes não encontrados na resposta', 'error');
          }
          
          errorsContext.addError(new CheckoutError('Detalhes do pagamento PIX não encontrados na resposta.'), { severity: 'error' });
          break;

        case PaymentMethodEnum.BOLETO:
          if (orderResponse.paymentData?.details) {
            return (
              <BoletoPaymentView
                paymentDetails={orderResponse.paymentData.details}
                customerName={`${orderResponse.customer.firstName} ${orderResponse.customer.lastName}`}
                totalAmount={value}
                offerNanoId={shopifyOffer.nanoId}
                shopifyData={shopifyOffer}
                fullData={orderResponse}
              />
            );
          }
          errorsContext.addError(new CheckoutError('Detalhes do pagamento por Boleto não encontrados na resposta.'), { severity: 'error' });
          break;

        case PaymentMethodEnum.CREDIT_CARD:
          return (
            <CreditCardView
              customerName={`${orderResponse.customer.firstName} ${orderResponse.customer.lastName}`}
              totalAmount={value}
              orderId={String(orderResponse.order.nanoId || orderResponse.order.id || '')}
              paymentMethod="Cartão de Crédito"
              offerNanoId={shopifyOffer.nanoId}
              shopifyData={shopifyOffer}
            />
          );

        default: // Typically for credit card success or other methods not requiring further action
          const paymentMethodName = (orderResponse.paymentMethod as any)?.paymentMethod || 'Pagamento';
          return (
            <CreditCardView
              customerName={`${orderResponse.customer.firstName} ${orderResponse.customer.lastName}`}
              totalAmount={value}
              orderId={String(orderResponse.order.nanoId || orderResponse.order.id || '')}
              paymentMethod={paymentMethodName}
              offerNanoId={shopifyOffer.nanoId}
              shopifyData={shopifyOffer}
            />
          );
      }
    }

    return null;
  };

  return renderPaymentView();
}; 